import type { DiamondType } from "./models";

export const WARMUP_SECONDS = 15;
export const ALERT_INTERVAL_SECONDS = 15;

export interface AlertState {
  level: number;
  pressure: number;
  label: string;
  hazardTarget: number;
  hazardPenalty: number;
  hazardSpeedScale: number;
  hazardSpawnInterval: number;
  threatSegments: number;
}

export const pressureFor = (survivalTime: number, bonusTimeEarned: number): number =>
  Math.max(0, survivalTime) + Math.max(0, bonusTimeEarned) * 0.75;

export const alertLevelFor = (survivalTime: number, bonusTimeEarned: number): number =>
  survivalTime < WARMUP_SECONDS
    ? 0
    : Math.max(1, Math.floor(pressureFor(survivalTime, bonusTimeEarned) / ALERT_INTERVAL_SECONDS));

export const alertLabelFor = (level: number): string => {
  if (level <= 0) return "Warmup";
  if (level <= 5) return `Alert ${level}`;
  if (level === 6) return "Lockdown 1";
  if (level === 7) return "Lockdown 2";
  return "Maximum Threat";
};

export const hazardPenaltyFor = (level: number): number =>
  level <= 0 ? 0 : Math.min(4, 1 + (level - 1) * 0.5);

export const alertStateFor = (
  survivalTime: number,
  bonusTimeEarned: number,
  campaignHazardBonus = 0,
): AlertState => {
  const level = alertLevelFor(survivalTime, bonusTimeEarned);
  return {
    level,
    pressure: pressureFor(survivalTime, bonusTimeEarned),
    label: alertLabelFor(level),
    hazardTarget: level <= 0 ? 0 : Math.min(8, level + Math.max(0, campaignHazardBonus)),
    hazardPenalty: hazardPenaltyFor(level),
    hazardSpeedScale: level <= 0 ? 0 : Math.pow(1.05, level - 1),
    hazardSpawnInterval: Math.max(0.8, 3.5 - level * 0.35),
    threatSegments: level <= 0 ? 0 : Math.min(10, level + 1),
  };
};

export const specialDiamondAvailable = (
  type: DiamondType,
  alertLevel: number,
  freezeOnCooldown = false,
): boolean => {
  switch (type) {
    case "SHIELD_DIAMOND": return alertLevel >= 1;
    case "CLEAN_DIAMOND": return alertLevel >= 2;
    case "FREEZE_DIAMOND": return alertLevel >= 3 && !freezeOnCooldown;
    case "TIME_DIAMOND": return alertLevel >= 4;
    default: return true;
  }
};
